import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormController,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../../../../../../../components";
import {
  MAX_LENGTH_1000,
  MAX_LENGTH_75,
} from "../../../../../../../../../../../../helpers/constants";
import {
  getVatPostalDetailsFromCountryKey,
  isNotEmpty,
} from "../../../../../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../../../../../hooks/useSiteCore";
import { ProgramClosureFormKey } from "../../../../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../../../../siteCore/SiteCoreKeys";
import { useNavigationSiteCore } from "../../../../../../../../../../../../hooks/useNavigationSiteCore";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../../../../../../../../../components/textFieldWithValidation/TextFieldWithValidation";
import { commonSiteCoreKeys } from "../../../../../../../../../../../../helpers/constants/siteCoreKeys";
import { getFieldRuleByCountryForLandingInformation } from "../../../../../../../../../../../../helpers/constants/countryFieldRulesUtils";

interface GeneralInformationProps {
  pageId: string;
  userCountryDateFormat: string;
  countryList: OptionsDataType[];
  programStartDate?: string;
  deliveryCountry?: string;
}

export const GeneralInformation: FC<GeneralInformationProps> = ({
  pageId,
  userCountryDateFormat,
  countryList,
  programStartDate = "",
  deliveryCountry = "",
}) => {
  const { control, getValues, setValue } = useFormContext();
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValidationMessage,
    fetchRichTexts,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const [countryVatPostalData, setCountryVatPostalData] = useState<
    CountryField[]
  >([]);

  const postalCodeRef = useRef<{setDefaultValue: (defaultValue: string) => void}>(null);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const countryIdWatch = useWatch({
    name: ProgramClosureFormKey.landingCountry,
    control,
  });

  const postalCodeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.postalCode,
  );

  const geographicMoveRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.geographicalMove,
  );

  const landingCountryRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.landingCountry,
  );

  const stateRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.state,
  );

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(programSiteCoreKeys.landedInformationPostalCode),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: 10,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
    isMandatory: false,
  };

  const countryVatPostalDataJson = fetchRichTexts(
    commonSiteCoreKeys.countryBusinessRuleListParent,
    commonSiteCoreKeys.countryBusinessRuleList,
  );

  useEffect(() => {
    if (countryVatPostalDataJson) {
      const countryVatPostalDataJsonParsed: CountryField[] = JSON.parse(
        countryVatPostalDataJson,
      );
      setCountryVatPostalData(countryVatPostalDataJsonParsed);
    }
  }, [countryVatPostalDataJson]);

  const postalCodeFieldDetails = useMemo(() => {
    const { landingCountry } = getValues();

    const postalData =
      getVatPostalDetailsFromCountryKey(landingCountry, countryVatPostalData)
        ?.postalCode || defaultCountryData;

    return {
      ...postalData,
      isMandatory: postalCodeRule.mandatory, // Override isMandatory as country specific
    };
  }, [countryIdWatch, countryVatPostalData, deliveryCountry]);

  useEffect(() => {
    setValue(
      ProgramClosureFormKey.landingCountry,
      landingCountryRule.visible ? deliveryCountry : "",
    );
  }, [deliveryCountry]);

  return (
    <>
      <MuiGrid
        container
        className="form-container"
        data-testid="general-information"
      >
        <MuiGrid size={gridSize} className="status-field">
          <MuiDateFieldFormController
            control={control}
            name={ProgramClosureFormKey.landedDate}
            className="date-picker"
            label={getLabel(programSiteCoreKeys.landedDate)}
            id={`${pageId}-${ProgramClosureFormKey.landedDate}`}
            disableManualInput
            {...(isNotEmpty(programStartDate) && {
              minDate: dayjs(programStartDate),
            })}
            disableFuture
            format={userCountryDateFormat}
            valueFormat={userCountryDateFormat}
            value={
              getValues(ProgramClosureFormKey.landedDate) !== ""
                ? dayjs(
                    getValues(ProgramClosureFormKey.landedDate),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.timeToLand}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.timeToLand}`}
              label={getLabel(programSiteCoreKeys.timeToLand)}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="form-container">
        {/* <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.totalHours}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.totalHours}`}
              label={getLabel(programSiteCoreKeys.totalHours)}
              disabled
            />
          </FormController>
        </MuiGrid> */}
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.totalNumberOfMeetings}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.totalNumberOfMeetings}`}
              label={getLabel(programSiteCoreKeys.totalNumberOfMeetings)}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="form-container">
        {geographicMoveRule.visible && (
          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={ProgramClosureFormKey.geographicalMove}
            >
              <MuiSelect
                id={`${pageId}-${ProgramClosureFormKey.geographicalMove}`}
                label={getLabel(programSiteCoreKeys.geographicMove)}
                placeholder={commonLabels.selectPlaceHolder}
                options={fetchDropdownValues(
                  programSiteCoreKeys.changeStatusModelPopupKey,
                  programSiteCoreKeys.geographicMoveList,
                )}
                visibleDefaultSelectOption
                requiredARIALabel={commonARIALabel.required}
                isMandatory={geographicMoveRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}
        {landingCountryRule.visible && (
          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={ProgramClosureFormKey.landingCountry}
            >
              <MuiSelect
                id={`${pageId}-${ProgramClosureFormKey.landingCountry}`}
                label={getLabel(programSiteCoreKeys.landingCountry)}
                placeholder={commonLabels.selectPlaceHolder}
                options={countryList}
                requiredARIALabel={commonARIALabel.required}
                showSearch
                searchPlaceholder={commonLabels.search}
                isMandatory={landingCountryRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}
        {stateRule.visible && (
          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={ProgramClosureFormKey.state}
            >
              <MuiTextField
                id={`${pageId}-${ProgramClosureFormKey.state}`}
                label={getLabel(programSiteCoreKeys.stateProvinceRegion)}
                placeholder={commonLabels.inputPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory={stateRule.mandatory}
                maxLength={MAX_LENGTH_75}
              />
            </FormController>
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.postalCode}
            ref={postalCodeRef}
          >
            <TextFieldWithValidation
              label={getLabel(programSiteCoreKeys.landedInformationPostalCode)}
              id={`${pageId}-${ProgramClosureFormKey.postalCode}`}
              formId={ProgramClosureFormKey.postalCode}
              isCountryChanged={countryIdWatch}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              requiredFieldMessage={fetchValidationMessage(
                commonSiteCoreKeys.requiredValidation,
              )}
              isFormReset={false}
              fieldDetails={postalCodeFieldDetails}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="form-container">
        <MuiGrid size={{ md: 8, sm: 8, xs: 12 }} className="form-field">
          <FormController control={control} name={ProgramClosureFormKey.notes}>
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.notes}`}
              label={getLabel(programSiteCoreKeys.notesGeneral)}
              placeholder={commonLabels.inputPlaceHolder}
              isTextarea
              multiline
              rows={4}
              maxLength={MAX_LENGTH_1000}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </>
  );
};
